import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * ChatVoiceNote — lecteur d'une note vocale envoyée dans le chat IA.
 *
 * Le message vocal s'affiche comme une note audio réécoutable (bouton lecture,
 * forme d'onde, temps écoulé / durée, recherche au clic). En coulisses, le
 * TEXTE transcrit est envoyé à l'IA — l'utilisateur ne voit que la voix.
 *
 * Un petit bouton repliable « Voir la transcription » permet de relire le texte
 * qui a réellement été envoyé à l'IA.
 */

interface ChatVoiceNoteProps {
  /** URL de lecture de l'enregistrement (blob local ou URL durable). */
  src: string;
  /** Durée connue de l'enregistrement (repli si les métadonnées tardent). */
  durationSeconds?: number;
  /** Texte transcrit envoyé à l'IA (relecture repliable). */
  transcript?: string;
}

function fmt(total: number): string {
  const safe = Number.isFinite(total) && total > 0 ? total : 0;
  const m = Math.floor(safe / 60);
  const s = Math.floor(safe % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Forme d'onde décorative (hauteurs fixes, purement visuelles). */
const BARS = [
  8, 14, 20, 12, 18, 24, 10, 16, 22, 14, 9, 17, 23, 13, 19, 11,
  15, 21, 12, 18, 10, 16, 22, 14, 20, 9, 17, 13, 19, 11, 15, 21,
];

export default function ChatVoiceNote({ src, durationSeconds = 0, transcript = '' }: ChatVoiceNoteProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(durationSeconds);
  const [showTranscript, setShowTranscript] = useState(false);

  // Réinitialise le lecteur quand la source change.
  useEffect(() => {
    setPlaying(false);
    setCurrent(0);
    setDuration(durationSeconds);
  }, [src, durationSeconds]);

  const toggle = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      void el.play();
    } else {
      el.pause();
    }
  }, []);

  const seekFromClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = audioRef.current;
    if (!el) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const total = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : duration;
    if (total > 0) {
      el.currentTime = ratio * total;
      setCurrent(el.currentTime);
    }
  }, [duration]);

  const total = Number.isFinite(duration) && duration > 0 ? duration : 0;
  const ratio = total > 0 ? Math.min(1, current / total) : 0;
  const filled = Math.round(ratio * BARS.length);
  const hasTranscript = transcript.trim().length > 0;

  return (
    <div className="flex flex-col gap-1.5 min-w-[200px] max-w-[280px]">
      <div className="flex items-center gap-2.5">
        <audio
          ref={audioRef}
          src={src}
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => { setPlaying(false); setCurrent(0); }}
          onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            if (Number.isFinite(d) && d > 0) setDuration(d);
          }}
          className="hidden"
        />

        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? 'Mettre en pause' : 'Lire la note vocale'}
          className="w-8 h-8 rounded-full bg-accent-600 text-background-50 flex items-center justify-center flex-shrink-0 cursor-pointer hover:bg-accent-700 transition-colors"
        >
          <i className={`${playing ? 'ri-pause-fill' : 'ri-play-fill'} text-sm`}></i>
        </button>

        <div
          onClick={seekFromClick}
          role="slider"
          tabIndex={0}
          aria-label="Position de lecture"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(ratio * 100)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
          className="flex-1 flex items-center gap-[2px] h-6 cursor-pointer"
          title="Cliquez pour vous déplacer dans la note"
        >
          {BARS.map((h, i) => (
            <span
              key={i}
              className={`w-[2px] rounded-full transition-colors ${i < filled ? 'bg-accent-600' : 'bg-foreground-300/70'}`}
              style={{ height: `${h}px` }}
            ></span>
          ))}
        </div>

        <span className="text-[10px] text-foreground-500 whitespace-nowrap flex-shrink-0 tabular-nums">
          {playing || current > 0 ? `${fmt(current)} / ${fmt(total)}` : fmt(total)}
        </span>
      </div>

      {hasTranscript && (
        <div className="border-t border-foreground-300/40 pt-1.5">
          <button
            type="button"
            onClick={() => setShowTranscript((v) => !v)}
            aria-expanded={showTranscript}
            className="flex items-center gap-1 text-[10px] font-medium text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
          >
            <i className={`${showTranscript ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`}></i>
            {showTranscript ? 'Masquer la transcription' : 'Voir la transcription'}
          </button>
          {showTranscript && (
            <p className="mt-1 text-[11px] leading-relaxed text-foreground-600 whitespace-pre-wrap break-words">
              {transcript}
            </p>
          )}
        </div>
      )}
    </div>
  );
}